
import { supabase } from "@/integrations/supabase/client";
import { canUserCopyPrompt } from "@/services/subscriptionService";
import { hasReachedPromptDailyCopyLimit, recordPromptCopy } from "@/services/promptCopyService";
import { featureFlags } from "@/utils/featureFlags";
import { toast } from "sonner";

export interface CopyResult {
  success: boolean;
  copied: boolean;
  countedInTotal: boolean;
  usageInfo: {
    canCopy: boolean;
    usedCount: number;
    limit: number;
    percentUsed: number;
  } | null;
  showUpgradeDialog: boolean;
}

export const copyPromptToClipboard = async (
  promptId: string,
  content: string,
  userId: string | undefined
): Promise<CopyResult> => {
  // Default result
  const result: CopyResult = {
    success: false,
    copied: false,
    countedInTotal: false,
    usageInfo: null,
    showUpgradeDialog: false
  };
  
  try {
    console.log(`Attempting to copy prompt: ${promptId} for user: ${userId || 'anonymous'}`);
    
    // Copy content to clipboard regardless of user status
    // This allows anonymous users to still use the prompts
    try {
      await navigator.clipboard.writeText(content);
      result.copied = true;
      console.log('Content copied to clipboard');
    } catch (clipboardErr) {
      console.error('Error copying to clipboard:', clipboardErr);
      toast.error('Could not copy to clipboard. Please try manually selecting and copying the text.');
      return result;
    }
    
    // If user is not logged in, we're done - no need to track usage or check limits
    if (!userId) {
      console.log('Anonymous copy - not tracking usage');
      return result;
    }

    // Since prompt copies are now unlimited, we always allow copying
    // But we still track usage for analytics
    const usageInfo = await canUserCopyPrompt(userId);
    result.usageInfo = usageInfo;
    console.log('User copy permissions:', usageInfo);
    
    // Prompt copies are unlimited, so no upgrade dialog needed
    result.showUpgradeDialog = false;
    
    // Check if user has reached their daily copy increment limit for this prompt
    let reachedDailyLimit = false;
    if (featureFlags.enableDailyCopyLimit) {
      reachedDailyLimit = await hasReachedPromptDailyCopyLimit(userId, promptId);
      console.log(`Has reached daily copy limit for this prompt: ${reachedDailyLimit}`);
    }
      
    // Record the copy in database, but only increment counts if within daily limit
    const shouldCountInTotal = !reachedDailyLimit;
    result.countedInTotal = shouldCountInTotal;
    console.log(`Will count in total: ${shouldCountInTotal}`);
    
    const copyRecorded = await recordPromptCopy(promptId, userId, shouldCountInTotal);
    
    if (copyRecorded) {
      result.success = true;
      console.log(`Copy recorded successfully. Counted in total: ${shouldCountInTotal}`);
      
      // Update the usage info
      const updatedUsageInfo = await canUserCopyPrompt(userId);
      result.usageInfo = updatedUsageInfo;
    } else {
      console.error('Failed to record prompt copy');
    }
  } catch (err) {
    console.error('Error in copyPromptToClipboard:', err);
    toast.error('Error copying prompt to clipboard');
  }
  
  return result;
};
